import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";

import { createApiClient } from "../api/client";
import type { AppConfig } from "../config";
import { classifyError, type Resource } from "./useResource";

/** One page, reduced to what appending needs: its rows and the next cursor. */
export interface PageOf<R> {
  rows: R[];
  /** Opaque. Sent back exactly as received; null means there is no more. */
  next: string | null;
}

function cursorOf(v: unknown): string | null {
  return typeof v === "string" && v !== "" ? v : null;
}

/** Labeeb's list envelope: `items` + `next_cursor` (labeeb.md §6). */
export function labeebPage<R>(page: unknown): PageOf<R> {
  const p = (page ?? {}) as { items?: unknown; next_cursor?: unknown };
  return { rows: Array.isArray(p.items) ? (p.items as R[]) : [], next: cursorOf(p.next_cursor) };
}

/** MCP's: the rows under a per-route key (`events`, `tenants`) + `cursor`. */
export function mcpPage<R>(field: string): (page: unknown) => PageOf<R> {
  return (page) => {
    const p = (page ?? {}) as Record<string, unknown>;
    const rows = p[field];
    return { rows: Array.isArray(rows) ? (rows as R[]) : [], next: cursorOf(p.cursor) };
  };
}

/**
 * A cursor-paged list that APPENDS.
 *
 * `useResource` replaces its payload on every path change, so putting the
 * cursor in the path would throw away the rows already on screen. This keeps
 * them and asks for the next page with `?cursor=` — the path may already carry
 * a query, which the client joins with `&`.
 *
 * `read` turns one response into rows and a cursor, so the two products'
 * envelopes stay in their own adapters. It is read through a ref: callers pass
 * an inline function, and a new identity each render must not refetch.
 *
 * A failed NEXT page leaves the rows already shown and reports `moreError`;
 * only a failed first page becomes the list's state.
 */
export function usePagedList<R>(
  config: AppConfig,
  path: string,
  read: (page: unknown) => PageOf<R>,
) {
  const auth = useAuth();
  const token = auth.user?.id_token;
  const [state, setState] = useState<Resource<PageOf<R>>>({ kind: "loading" });
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<string | null>(null);
  const readRef = useRef(read);
  readRef.current = read;
  // Bumped on every fresh load. A response from an older generation — a
  // loadMore or reload that was in flight when the path changed — must not
  // land on the list the new path is showing.
  const generation = useRef(0);

  const fetchPage = useCallback(
    async (cursor: string | null): Promise<PageOf<R>> => {
      const api = createApiClient({ apiUrl: config.apiUrl, getIdToken: () => token });
      const page = await api.request<unknown>(path, { query: cursor ? { cursor } : undefined });
      return readRef.current(page);
    },
    [config.apiUrl, token, path],
  );

  const load = useCallback(() => {
    const gen = ++generation.current;
    setState({ kind: "loading" });
    setMoreError(null);
    setLoadingMore(false);
    fetchPage(null).then(
      (data) => {
        if (generation.current !== gen) return;
        setState({ kind: "ready", data });
      },
      (err: unknown) => {
        if (generation.current !== gen) return;
        setState(classifyError(err));
      },
    );
    return () => {
      // The effect is re-running (path change or unmount). Drop this
      // generation so its response cannot overwrite the next load. A reload
      // that already advanced the counter is left for that newer generation
      // to finish — the next load() call invalidates it in turn.
      if (generation.current === gen) generation.current += 1;
    };
  }, [fetchPage]);

  useEffect(load, [load]);

  const reload = useCallback(() => {
    load();
  }, [load]);

  const loadMore = useCallback(() => {
    if (state.kind !== "ready" || !state.data.next || loadingMore) return;
    const gen = generation.current;
    setLoadingMore(true);
    setMoreError(null);
    fetchPage(state.data.next)
      .then((page) => {
        if (generation.current !== gen) return;
        setState((prev) =>
          prev.kind === "ready"
            ? { kind: "ready", data: { rows: [...prev.data.rows, ...page.rows], next: page.next } }
            : prev,
        );
      })
      .catch((err: unknown) => {
        if (generation.current !== gen) return;
        const classified = classifyError(err);
        setMoreError(
          classified.kind === "signedout"
            ? "Signed out"
            : "error" in classified
              ? classified.error.message
              : "Could not load more rows",
        );
      })
      .finally(() => {
        if (generation.current === gen) setLoadingMore(false);
      });
  }, [fetchPage, loadingMore, state]);

  return { state, reload, loadMore, loadingMore, moreError };
}
